/**
 * DI-2-3 — small fetch helper for the `inspection-3/v1` namespace, the write
 * surface the intake creates its Data Acquisitions on (DI-D34 c1).
 *
 * Mirrors `timeTrackingFetch.ts`: the surface is TENANT-SCOPED, so every call
 * sends `X-Tenant-ID` alongside the bearer token. Base URL comes from
 * `getInspection3BaseUrl()` so `DEV_API_MODE` routes requests correctly.
 * Errors carry the parsed JSON body so callers can read the envelope — in
 * particular the 422 `reason` (`unknown_workflow`, `workflow_not_granted`)
 * and the 409 `reason` (`external_id_conflict`).
 */
import { getInspection3BaseUrl, useAuthStore } from "@aeriosiq/auth-client";
import { tenantForCall } from "./setTenants.js";
import { INTAKE_REQUEST_DEADLINE_MS, withDeadline } from "./deadline.js";

export interface Inspection3FetchError extends Error {
  status: number;
  body: unknown;
}

interface Inspection3FetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /**
   * M3C-04 — the tenant this call is for: a string names it (a create under
   * a shared injection node, a list run for a shared owner); null forces the
   * caller's own; absent → the tenant of the set the path addresses
   * (`setTenants.ts`), else the caller's own.
   */
  tenantSlug?: string | null;
  /**
   * DI-3-S68 B1 — this request kind's deadline (default
   * `INTAKE_REQUEST_DEADLINE_MS`); past it the call rejects with
   * `RequestTimeoutError`, a transport failure (`deadline.ts`).
   */
  timeoutMs?: number;
}

/**
 * The ONE place (with its sibling helper) a set's tenant becomes the
 * `X-Tenant-ID` header (lock `one-header-place`, M3C-04 AC-4): the set's
 * `targetTenantSlug` when it has one, else the auth store's slug.
 */
function authHeaders(target: string | null): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  const slug = target ?? tenant?.tenantSlug ?? null;
  if (slug) {
    headers.set("X-Tenant-ID", slug);
  }
  return headers;
}

export async function inspection3Fetch<T>(
  path: string,
  opts: Inspection3FetchOptions = {},
): Promise<T> {
  const headers = authHeaders(tenantForCall(path, opts.tenantSlug));
  const init: RequestInit = { method: opts.method ?? "GET", headers };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
    headers.set("Content-Type", "application/json");
  }
  return withDeadline(
    path,
    opts.timeoutMs ?? INTAKE_REQUEST_DEADLINE_MS,
    async (signal) => {
      init.signal = signal;
      const res = await fetch(`${getInspection3BaseUrl()}${path}`, init);
      return readAnswer<T>(path, res);
    },
  );
}

/** The answer: the parsed body, or the error carrying status + envelope. */
async function readAnswer<T>(path: string, res: Response): Promise<T> {
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // Non-JSON error body — the status still surfaces.
    }
    const err = new Error(
      `inspection-3 fetch failed: ${path} → ${String(res.status)}`,
    ) as Inspection3FetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  const text = await res.text();
  if (text === "") return undefined as unknown as T;
  return JSON.parse(text) as T;
}

export function isInspection3FetchError(
  e: unknown,
): e is Inspection3FetchError {
  return (
    e instanceof Error &&
    typeof (e as Partial<Inspection3FetchError>).status === "number"
  );
}
